import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { cleanStationName } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import { readAnyCachedStationIdentity, readCachedStationIdentity, subscribeStationIdentity, writeCachedStationIdentity } from "../util/stationIdentityCache";

// #3508 (station-monitor workstream) -- is THIS tablet a named station?
//
// A station is one GUID on one machine (the GUID is presenceInstanceId); its
// name is chosen once at setup. This hook answers "has it been named?":
//
//   named        the browser remembers a name the server confirmed, or the
//                server just told us (GET /station/:id -> 200)
//   needs-name   the server says it has never been named (404): show the name step
//   unreachable  we could not ask (offline, timeout, 5xx). NOT the same as
//                needs-name: the caller must not block a tablet that is
//                mid-show on a network blip.
//   loading      first answer pending
//
// #3632: the name is stored per org on the server, but a tablet is named once.
// When a new org has no row (404) and this browser already holds a confirmed name
// for this GUID in another org, that name is PUT to the new org in the background
// and the station counts as named. The name step is shown only when the machine
// has no name anywhere, another station in the new org already uses the name, or
// the server refuses it (4xx). A PUT that fails for any other reason (offline,
// 5xx) is "unreachable", the same as a failed GET.
//
// A cached name short-circuits everything, so a named tablet never waits on
// the network at load. No background retry: an answer that arrives after the
// caller already let the tablet through must not pull a half-scored sheet out
// from under a scribe. The next load asks again.
const ASK_TIMEOUT_MS = 6000;

const isNotFound = (err) => err?.response?.status === 404;

// #3632: the new org has no row for this GUID. Resolves { identity } when the
// machine's known name was saved to the new org, else { status } ("needs-name"
// or "unreachable"). The org the calls run under is the ApiConfig's active org.
async function carryKnownName(scoreApi, isCancelled) {
  const known = readAnyCachedStationIdentity();
  if (!known) return { status: "needs-name" };
  const guid = getPresenceInstanceId();
  try {
    const found = unwrap(await scoreApi.stations.checkStationName(known.name, guid));
    if (Array.isArray(found.matches) && found.matches.length) return { status: "needs-name" };
  } catch {
    // Best effort, like the name step's own warning: the PUT below is the real answer.
  }
  if (isCancelled()) return { status: "needs-name" };
  try {
    const data = unwrap(await scoreApi.stations.putStation(guid, { name: known.name, isTest: !!known.isTest }));
    return { identity: { name: data.station.name, isTest: !!data.station.isTest } };
  } catch (err) {
    const code = err?.response?.status;
    return { status: code >= 400 && code < 500 ? "needs-name" : "unreachable" };
  }
}

/**
 * #3520: the remembered name of THIS station in the org, as a string (or null).
 * Reactive: a heartbeat that swaps in an admin-renamed name re-renders the caller.
 * Display only (the header); the name gate keeps using useStationIdentity.
 */
export function useStationName(orgId) {
  return useSyncExternalStore(
    subscribeStationIdentity,
    () => (orgId ? readCachedStationIdentity(orgId)?.name ?? null : null),
    () => null
  );
}

export function useStationIdentity(orgId) {
  const scoreApi = useScoreApi();
  const cached = orgId ? readCachedStationIdentity(orgId) : null;
  const [state, setState] = useState(() => ({
    orgId,
    status: !orgId ? "loading" : cached ? "named" : "loading",
    identity: cached,
  }));
  const [retry, setRetry] = useState(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A different org is a different identity row: re-resolve from scratch.
  useEffect(() => {
    if (!orgId) return undefined;
    const hit = readCachedStationIdentity(orgId);
    if (hit) {
      setState({ orgId, status: "named", identity: hit });
      return undefined;
    }
    let cancelled = false;
    setState({ orgId, status: "loading", identity: null });
    scoreApi.stations
      .getStation(getPresenceInstanceId(), { timeout: ASK_TIMEOUT_MS })
      .then((res) => {
        if (cancelled) return;
        const station = unwrap(res);
        const identity = { name: station.name, isTest: !!station.isTest };
        writeCachedStationIdentity(orgId, identity);
        setState({ orgId, status: "named", identity });
      })
      .catch(async (err) => {
        if (cancelled) return;
        if (!isNotFound(err)) {
          setState({ orgId, status: "unreachable", identity: null });
          return;
        }
        const carried = await carryKnownName(scoreApi, () => cancelled);
        if (cancelled) return;
        if (carried.identity) {
          writeCachedStationIdentity(orgId, carried.identity);
          setState({ orgId, status: "named", identity: carried.identity });
        } else {
          setState({ orgId, status: carried.status, identity: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, scoreApi, retry]);

  /** Ask again (the "can't reach the server" screen's button). */
  const recheck = useCallback(() => setRetry((n) => n + 1), []);

  /** Is another station in this org already using this name (seen in the last 7 days)? Best effort: [] on any failure. */
  const checkName = useCallback(
    async (name) => {
      const clean = cleanStationName(name);
      if (!clean) return [];
      try {
        const data = unwrap(await scoreApi.stations.checkStationName(clean, getPresenceInstanceId()));
        return Array.isArray(data.matches) ? data.matches : [];
      } catch {
        return [];
      }
    },
    [scoreApi]
  );

  /** Name this tablet. Resolves true when the server has it; false (with nothing stored) when it could not be saved. */
  const saveName = useCallback(
    async ({ name, isTest }) => {
      const clean = cleanStationName(name);
      if (!clean) return false;
      try {
        const data = unwrap(await scoreApi.stations.putStation(getPresenceInstanceId(), { name: clean, isTest: !!isTest }));
        const identity = { name: data.station.name, isTest: !!data.station.isTest };
        writeCachedStationIdentity(orgId, identity);
        if (alive.current) setState({ orgId, status: "named", identity });
        return true;
      } catch {
        return false;
      }
    },
    [scoreApi, orgId]
  );

  // The state may still describe the previous org for one render.
  const current = state.orgId === orgId ? state : { status: "loading", identity: null };
  return { status: current.status, identity: current.identity, saveName, checkName, recheck };
}
